<template>
  <!-- RQ Data Sheet card (Android FragmentDataSheet1…4) on the data sheet page: 4-step wizard, draft saved on every
       step change; a submitted sheet shows as the server page first (Edit / View, View only when readOnly) -->
  <div ref="body" class="rqCard font-roboto flex flex-col">
    <div class="flex flex-wrap items-center justify-between gap-2 pb-[10px]">
      <div class="flex items-center gap-1">
        <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
        <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
        <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">Data Sheet</p>
      </div>
      <span v-if="view === 'wizard'" class="text-[14px] text-[#7695FF]">Step {{ step }} of 4</span>
      <!-- a sheet was already submitted (Android bottom sheet "Select an option") -->
      <div v-else-if="url && !readOnly" class="flex gap-[10px]">
        <button type="button" class="fxBtnPrimary" @click="view = 'wizard'">Edit</button>
      </div>
    </div>

    <p v-if="!loaded" class="m-0 py-[30px] text-center text-[13px] text-[#999999]">Loading…</p>
    <iframe v-else-if="view === 'view'" :src="url" title="Data Sheet" class="w-full h-[75vh] border-0 rounded-[8px]" />

          <div v-else-if="view === 'wizard'" :key="openCount" class="flex-1 flex flex-col gap-[12px] px-[6px] pt-[12px] pb-[12px]">
            <p class="m-0 text-[14px] text-[#666666]">Please enter the following details</p>
            <div v-for="d in visible" :id="'rqds_' + d.key" :key="d.key">
              <template v-if="d.kind === 'heading'">
                <p class="m-0 text-[15px] font-medium text-[#333333]">{{ d.label }}</p>
                <p v-if="d.hint" class="m-0 text-[13px] text-[#999999]">{{ d.hint }}</p>
              </template>
              <template v-else>
                <div class="uqcLabel">{{ d.label }} <span v-if="d.msg" class="text-[#EE5851]">*</span></div>
                <p v-if="d.hint" class="m-0 mb-[6px] text-[13px] leading-[18px] text-[#666666]">{{ d.hint }}</p>
                <inputComponent v-if="d.kind === 'text'" :ref="el => setRef(d.key, el)" v-model="form[d.key]" :name="'rqds_in_' + d.key"
                  :placeholder="d.placeholder" :required="!!d.msg" :required-message="d.msg" :mask="d.mask" :regex="d.regex"
                  :error-message="d.err" :inputmode="d.inputmode" :custom-class="fieldClass" />
                <FxSelect v-else-if="d.kind === 'select'" v-model="form[d.key]" :options="d.list.map(v => ({ label: v, value: v }))"
                  :placeholder="'Select ' + d.label" inline @change="errors[d.key] = ''" />
                <textarea v-else-if="d.kind === 'textarea'" v-model="form[d.key]" rows="3" :placeholder="d.placeholder"
                  class="w-full rounded-[8px] border border-[#E1E5EF] px-[12px] py-[8px] text-[14px] text-[#333333] outline-none resize-none"
                  @input="errors[d.key] = ''" />
                <date-picker v-else-if="d.kind === 'date'" :value="form.installation_date" class="rqdsDate" type="datetime"
                  :format="FMT" value-type="format" placeholder="Select Date" :minute-step="15" :hour-options="hourOptions"
                  :disabled-date="disabledDate" :clearable="false" :editable="false" :confirm="true" :append-to-body="false"
                  @change="onDate" />
                <p v-if="errors[d.key] && d.kind !== 'text'" class="text-[#EE5851] text-xs mt-1 mb-0">{{ errors[d.key] }}</p>
              </template>
            </div>
          </div>

          <div v-if="loaded && view === 'wizard'" class="flex items-center gap-[10px] pt-[15px] border-t border-[#E1E5EF]">
            <button v-if="step > 1" type="button" class="uqcBtnBack" aria-label="Back" @click="go(-1)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M19 12H5m6-6-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
            </button>
            <div class="flex-1 flex justify-end gap-[10px]">
              <button type="button" class="uqcBtnCancel" @click="cancel">Cancel</button>
              <button v-if="step < 4" type="button" class="uqcBtnNext" @click="go(1)">Next</button>
              <button v-else type="button" class="uqcBtnNext" @click="confirmSubmit">Submit</button>
            </div>
          </div>
  </div>
</template>

<script>
import moment from "moment";
import DatePicker from "vue-datepicker-next";
import "vue-datepicker-next/index.css";
import { useToast } from "vue-toastification";
import FxSelect from "../partialComponent/FxSelect.vue";
import inputComponent from "../partialComponent/inputComponent.vue";
import { rqApi } from "@/services/rqApi";

const toast = useToast();
// Android DATE_SERVER_FORMAT_ORDER_MODEL "MM/dd/yyyy hh:mm a"; Laravel parses it as 'm/d/Y h:i A'
const FMT = "MM/DD/YYYY hh:mm A";
const YES_NO = ["Yes", "No"];
const FLOOD = ["Yes", "No", "Others"];
const NUM = /^\d+(\.\d+)?$/;
// DataSheetModelRequest / orders/sheet/* field names (Android "notes" goes out as free_notes)
const KEYS = ["name", "street", "city", "state", "zip", "phone", "email", "installation_date", "flood_zone",
  "existing_brand", "new_brand", "existing_system_type", "new_system_type", "incoming_voltage", "seer", "thermostat", "filtration",
  "existing_capacity", "new_capacity", "existing_heat_type", "existing_furnace_btu", "new_heat_type", "new_furnace_btu", "new_heat_strip",
  "filter_location", "filter_type", "uv_light", "existing_ah_location", "new_ah_location", "existing_ah_mount", "new_ah_mount",
  "ah_wire_size", "ah_brkr_size", "brkr_panel_brand", "existing_lineset_size", "new_lineset", "new_lineset_size", "new_lineset_length",
  "ah_height", "ah_width", "ah_depth", "inside_restrictions", "inside_restriction_notes", "outside_restrictions",
  "outside_restrictions_notes", "ah_notes", "emergency_pan", "existing_cu_pkg_location", "new_cu_pkg_location",
  "existing_cu_pkg_mount", "new_cu_pkg_mount", "new_pad", "control_wire_conductors", "new_stat_type", "cu_pkg_brkr_size",
  "payment_method", "atwood_funding_amt", "greensky_plan", "total_contract_amt", "system_add_ons", "free_notes"];
const furnaceOrBoiler = (v) => ["furnace", "boiler"].includes(String(v || "").toLowerCase());
const isYes = (v) => String(v || "").toLowerCase() === "yes";
const formatPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : d;
};

export default {
  components: { FxSelect, inputComponent, DatePicker },
  props: {
    orderId: { type: [Number, String], required: true },
    order: { type: Object, default: () => ({}) },
    readOnly: { type: Boolean, default: false },
  },
  emits: ["submitted", "cancel"],
  data() {
    return {
      FMT,
      view: "wizard",
      loaded: false,
      step: 1,
      opts: {},
      url: "",
      form: { flood_choice: "", flood_text: "" },
      errors: {},
      openCount: 0,
      submitted: false,
      // Android time picker: 07:00 – 21:00, 15-minute steps
      hourOptions: Array.from({ length: 15 }, (_, i) => i + 7),
      fieldClass: "!h-[40px] !px-[12px] !rounded-[8px] !border-[#E1E5EF] !text-[14px] !text-[#333333]",
    };
  },
  computed: {
    steps() {
      const f = this.form;
      const o = (k) => (Array.isArray(this.opts[k]) ? this.opts[k].map(String) : []);
      const sel = (key, label, list, msg, extra = {}) => ({ key, label, kind: "select", list, msg, show: true, ...extra });
      const txt = (key, label, placeholder, msg, extra = {}) => ({ key, label, kind: "text", placeholder, msg, show: true, ...extra });
      const num = (key, label, placeholder, msg, extra = {}) =>
        txt(key, label, placeholder, msg, { regex: NUM, err: "Please enter a valid number", inputmode: "decimal", ...extra });
      const area = (key, label, placeholder, msg, extra = {}) => ({ key, label, kind: "textarea", placeholder, msg, show: true, ...extra });
      const head = (label, hint) => ({ key: "h_" + label, kind: "heading", label, hint, show: true });
      const pm = String(f.payment_method || "").toLowerCase();
      return [
        [
          txt("name", "Client Name", "Enter Client Name", "Client Name Required"),
          head("Address"),
          txt("street", "Street", "Enter Street", "Street Field Required"),
          txt("city", "City", "Enter City", "City Field Required"),
          txt("state", "State", "Enter State", "State Field Required"),
          txt("zip", "Zip", "Enter Zip", "Zip Field Required", { mask: "#####", regex: /^\d{5}$/, err: "Zip Field Not Valid", inputmode: "numeric" }),
          txt("phone", "Phone Number", "Enter Phone Number", "Phone Number Field Required", { mask: "(###) ###-####", err: "Phone Number Not Valid", inputmode: "tel" }),
          txt("email", "Email Address", "Enter Email Address", "Email Field Required", { regex: this.$constants.EMAIL_REGEX, err: "Invalid Format Email." }),
          { key: "installation_date", label: "Installation Date", kind: "date", msg: "Installation Date Required", show: true },
          sel("flood_choice", "Flood Zone", FLOOD, "Flood Zone Field Required"),
          txt("flood_text", "Please Specify", "Specify Flood Zone", "Flood Notes Field Required", { show: f.flood_choice === "Others" }),
        ],
        [
          txt("existing_brand", "Existing Brand", "Enter Existing Brand Name", "Existing Brand Field Required"),
          txt("new_brand", "New Brand", "Enter New Brand Name", "New Brand Field Required"),
          sel("existing_system_type", "Existing System Type", o("system_type"), "Existing System Type Field Required"),
          sel("new_system_type", "New System Type", o("system_type"), "New System Type Field Required"),
          sel("incoming_voltage", "Incoming Voltage", o("incoming_voltage"), "Incoming Voltage field Required"),
          sel("seer", "SEER", o("seer"), "SEER Field Required"),
          sel("thermostat", "Thermostat", o("thermostat"), "Thermostat Field Required"),
          sel("filtration", "Filtration", o("filtration"), "Filtration Field Required"),
          sel("existing_capacity", "Existing Capacity", o("capacity"), "Existing Capacity Field Required"),
          sel("new_capacity", "New Capacity", o("capacity"), "New Capacity Field Required"),
          sel("existing_heat_type", "Existing Heat Type", o("heat_type"), "Existing Heat Type Field Required"),
          sel("existing_furnace_btu", "Existing Furnace BTU", o("existing_furnace_btu"), "Existing Furnace BTU Field Required",
            { show: this.showBtu("existing") }),
          sel("new_heat_type", "New Heat Type", o("heat_type"), "New Heat Type Field Required"),
          sel("new_furnace_btu", "New Furnace BTU", o("new_furnace_btu"), "New Furnace BTU Field Required", { show: this.showBtu("new") }),
          sel("new_heat_strip", "New Heat Strip Kw", o("new_heat_strip"), "New Heat Stripe Field Required",
            { show: !furnaceOrBoiler(f.new_heat_type) }),
        ],
        [
          sel("filter_location", "Filter Location", o("filter_location"), "Filter Location Field Required"),
          sel("filter_type", "Filter Type", o("filter_type"), "Filter type Field Required"),
          sel("uv_light", "UV Light", o("uv_light"), "UV Light Field Required"),
          sel("existing_ah_location", "Existing AH Location", o("ah_location"), "Existing AH Location Field Required"),
          sel("new_ah_location", "New AH Location", o("ah_location"), "New AH Location Field Required"),
          sel("existing_ah_mount", "Existing AH Mount", o("ah_mount"), "Existing AH Mount Field Required"),
          sel("new_ah_mount", "New AH Mount", o("ah_mount"), "New Existing AH Mount Field Required"),
          sel("ah_wire_size", "AH Wire Size", o("ah_wire_size"), "AH Wire Size Field Required"),
          num("ah_brkr_size", "AH Brkr Size", "Enter AH Brkr Size", "AH Brkr Size Field Required"),
          txt("brkr_panel_brand", "Brkr Panel Brand", "Enter Brkr Panel Brand", "AH Brkr Panel Brand Field Required"),
          txt("existing_lineset_size", "Existing Lineset Size", "Enter Existing Lineset Size", "Existing Lineset Field Required"),
          sel("new_lineset", "New Lineset", o("new_lineset"), "New Lineset Field Required"),
          txt("new_lineset_size", "New Lineset Size", "Enter New Lineset Size", "New Lineset Size Field Required", { show: isYes(f.new_lineset) }),
          txt("new_lineset_length", "New Lineset Length", "Enter New Lineset Length", "New Lineset Length Field Required",
            { show: isYes(f.new_lineset) }),
          head("AH Restrictions", "HxWxD in inches"),
          num("ah_height", "Height", "Enter Height", "Height Field Required"),
          num("ah_width", "Width", "Enter Width", "Width Field Required"),
          num("ah_depth", "Depth", "Enter Depth", "Depth Field Required"),
          sel("inside_restrictions", "Inside Restrictions", o("inside_restrictions"), "Inside Restrictions Field Required", {
            hint: "Are there any access/measurement restrictions on the inside of the house? (i.e. closet where AH is going may require trim removal to fit)",
          }),
          area("inside_restriction_notes", "Notes", "Enter Notes", "", { show: isYes(f.inside_restrictions) }),
          sel("outside_restrictions", "Outside Restrictions", o("outside_restrictions"), "Outside Restrictions Field Required", {
            hint: "Are there any access/measurement restrictions on the outside of the house? (i.e. the AH going to be hung in garage and requires an AH scissor lift)",
          }),
          area("outside_restrictions_notes", "Notes", "Enter Notes", "", { show: isYes(f.outside_restrictions) }),
          area("ah_notes", "AH Notes", "Enter Text", "Notes Field Required"),
        ],
        [
          sel("emergency_pan", "Need Emergency Pan?", YES_NO, "Need Emergency Pan? Field Required"),
          sel("existing_cu_pkg_location", "Existing CU/PKG Location", o("cu_pkg_location"), "Existing CU/PKG Location Field Required"),
          sel("new_cu_pkg_location", "New CU/PKG Location", o("cu_pkg_location"), "New CU/PKG Location Field Required"),
          sel("existing_cu_pkg_mount", "Existing CU/PKG Mount", o("cu_pkg_mount"), "Existing CU/PKG Mount Field Required"),
          sel("new_cu_pkg_mount", "New CU/PKG Mount", o("cu_pkg_mount"), "New CU/PKG Mount Field Required"),
          sel("new_pad", "New Pad", YES_NO, "New Pad Field Required"),
          sel("control_wire_conductors", "Control Wire Conductors", o("control_wire_conductors"), "Control Wire Conductors Field Required"),
          sel("new_stat_type", "New Stat Type?", o("new_stat_type"), "New Stat Type? Field Required"),
          num("cu_pkg_brkr_size", "CU/PKG Brkr Size", "Select CU/PKG Brkr Size", "CU/PKG Brkr Size Field Required"),
          sel("payment_method", "Payment Method", o("payment_method"), "Payment Method Field Required"),
          num("atwood_funding_amt", "If Atwood Funding Amount", "Select If Atwood Funding Amt", "If Atwood Funding Amount Field Required",
            { show: pm === "atwood" }),
          sel("greensky_plan", "GreenSky Plan Id", o("greensky_plans"), "GreenSky Plan Field Required", { show: pm === "gs" }),
          num("total_contract_amt", "Total Contract Amount", "Total Contract Amount", "Total Contract Amount Field Required"),
          area("free_notes", "Notes", "Please Enter Notes Here", ""),
          area("system_add_ons", "System Add-Ons?", "Enter Text", "System Add-Ons? Field Required"),
        ],
      ];
    },
    visible() {
      return this.steps[this.step - 1].filter(d => d.show);
    },
  },
  created() {
    this.inputRefs = {};
  },
  // rendered once the page has the order (prefill)
  mounted() {
    this.load();
  },
  // Android saves the draft whenever a step is left, also when backing out of the wizard
  beforeUnmount() {
    if (this.loaded && this.view === "wizard" && !this.submitted && !this.readOnly) this.saveDraft();
  },
  methods: {
    async load() {
      const res = await rqApi.dataSheet(this.orderId);
      if (!res.ok) {
        toast.error(res.message || "Unable to load the data sheet.");
        return this.$emit("cancel");
      }
      const body = res.body || {};
      this.opts = body;
      this.url = body.url || "";
      this.fill(body.existing_data);
      this.step = 1;
      this.errors = {};
      this.submitted = false;
      this.openCount++;
      // submitted sheet: show it first (Edit switches to the wizard)
      this.view = this.url ? "view" : "wizard";
      this.loaded = true;
    },
    cancel() {
      this.$emit("cancel");
    },
    // existing_data = the saved draft / sheet, with the order's consumer, address, total, payment type as fallback
    fill(existing) {
      const e = existing || {};
      const f = Object.fromEntries(KEYS.map(k => [k, e[k] == null ? "" : String(e[k])]));
      const c = this.order?.consumer || {};
      const a = this.order?.address || {};
      f.name = f.name || c.full_name || "";
      f.email = f.email || c.email || "";
      f.street = f.street || a.address || "";
      f.city = f.city || a.city || "";
      f.state = f.state || a.state || "";
      f.zip = f.zip || a.postal_code || "";
      f.phone = formatPhone(f.phone || c.phone);
      const ts = Number(e.new_installation_date);
      f.installation_date = ts ? moment.unix(ts).format(FMT) : (moment(f.installation_date, FMT, true).isValid() ? f.installation_date : "");
      // flood zone: Yes / No, anything else is the "Others" text
      const fz = f.flood_zone;
      f.flood_choice = !fz ? "" : (YES_NO.includes(fz) ? fz : "Others");
      f.flood_text = f.flood_choice === "Others" ? fz : "";
      const pms = Array.isArray(this.opts.payment_method) ? this.opts.payment_method : [];
      f.payment_method = pms.find(p => String(p).toLowerCase() === f.payment_method.toLowerCase()) || f.payment_method;
      this.form = f;
    },
    setRef(key, el) {
      if (el) this.inputRefs[key] = el;
      else delete this.inputRefs[key];
    },
    showBtu(which) {
      const list = this.opts[which + "_furnace_btu"];
      return furnaceOrBoiler(this.form[which + "_heat_type"]) && Array.isArray(list) && list.length > 0;
    },
    disabledDate(d) {
      return d < moment().startOf("day").toDate() || d > moment().add(29, "days").endOf("day").toDate();
    },
    onDate(value) {
      this.errors.installation_date = "";
      const d = moment(value, FMT);
      const cc = String(this.order?.preferred_payment_type?.type || "").toLowerCase() === "cc";
      const limit = moment().add(5, "days").set({ hour: d.hour(), minute: d.minute(), second: 0 });
      if (!cc || !d.isAfter(limit)) {
        this.form.installation_date = value;
        return;
      }
      // Android: Yes keeps the date, No clears it (here: closing the popup keeps the previous date)
      this.$dialog.show({
        title: "Alert",
        text: `The installation date selected is ${d.format("MM/DD/YYYY")}. If the mode of payment selected by the consumer is Credit Card, then this selection will expire the credit card capture that is valid for 6 days from the date of transaction. Payment for this order must be settled separately. Do you want to continue?`,
        buttonText: "Yes",
        onConfirm: () => { this.form.installation_date = value; },
      });
    },
    validateStep() {
      const errors = {};
      this.visible.forEach(d => {
        if (d.kind === "heading" || (!d.msg && d.kind !== "text")) return;
        if (d.kind === "text") {
          const msg = this.inputRefs[d.key]?.validate();
          if (msg) errors[d.key] = msg;
          return;
        }
        const v = String(this.form[d.key] ?? "").trim();
        // a prefilled value that isn't in the list counts as not selected (Android setSelectedIndex(-1))
        if (d.kind === "select" ? !d.list.includes(v) : !v) errors[d.key] = d.msg;
      });
      this.errors = errors;
      const first = Object.keys(errors)[0];
      if (first) document.getElementById("rqds_" + first)?.scrollIntoView({ block: "center", behavior: "smooth" });
      return !first;
    },
    payload() {
      const f = { ...this.form };
      f.flood_zone = f.flood_choice === "Others" ? f.flood_text : f.flood_choice;
      delete f.flood_choice;
      delete f.flood_text;
      Object.keys(f).forEach(k => { f[k] = String(f[k] ?? "").trim(); });
      const d = moment(f.installation_date, FMT, true);
      f.new_installation_date = d.isValid() ? String(d.unix()) : "";
      return f;
    },
    saveDraft() {
      // failure is silent: the draft is only a convenience, the final submit sends everything again
      rqApi.saveDataSheetDraft(this.orderId, this.payload());
    },
    go(delta) {
      if (delta > 0 && !this.validateStep()) return;
      this.saveDraft();
      this.step += delta;
      this.errors = {};
      this.$refs.body.scrollIntoView({ block: "start", behavior: "smooth" });
    },
    confirmSubmit() {
      if (!this.validateStep()) return;
      this.saveDraft();
      this.$dialog.show({
        title: "Confirmation",
        text: "Are you sure you want to submit this data sheet?",
        buttonText: "Yes",
        onConfirm: () => this.submit(),
      });
    },
    // same fields as Android FragmentDataSheet4.getActionSheetData: hidden fields are left out (or empty)
    async submit() {
      const f = this.payload();
      if (!this.showBtu("existing")) f.existing_furnace_btu = "";
      if (!this.showBtu("new")) f.new_furnace_btu = "";
      if (furnaceOrBoiler(f.new_heat_type)) f.new_heat_strip = "";
      if (!isYes(f.new_lineset)) { delete f.new_lineset_size; delete f.new_lineset_length; }
      if (!isYes(f.inside_restrictions)) delete f.inside_restriction_notes;
      if (!isYes(f.outside_restrictions)) delete f.outside_restrictions_notes;
      const pm = f.payment_method.toLowerCase();
      if (pm !== "atwood") delete f.atwood_funding_amt;
      // Laravel greensky_plan has an `in:` rule that also fails on an empty value
      if (pm !== "gs") delete f.greensky_plan;
      const res = await rqApi.submitDataSheet(this.orderId, f);
      if (!res.ok) return toast.error(res.message || "Unable to submit the data sheet.");
      this.submitted = true;
      toast.success("Data sheet has been submitted successfully");
      this.$emit("submitted", res.body);
    },
  },
};
</script>

<style scoped>
/* the date field looks like the other 40px inputs and opens right under it (as UQAddUnit .uqDate) */
.rqdsDate {
  position: relative;
  width: 100%;
}
.rqdsDate :deep(.mx-input) {
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #E1E5EF;
  font-size: 14px;
  color: #333333;
  box-shadow: none;
}
.rqdsDate :deep(.mx-datepicker-popup) {
  top: 100% !important;
  left: 0 !important;
  bottom: auto !important;
  margin-top: 4px;
  z-index: 20;
}
</style>
